import { saveListExcel } from 'utils/tableExport/saveListExcel';
import { isRequestError } from 'utils/requestError';
import { getImageSource } from 'utils/helpers/imageSource';
import { ImageModal } from 'components/ImageModal';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { DEFAULT_PAGE } from 'configs';
import { format } from 'date-fns';
import { InputText, InputSelect, Card, TableListData } from 'components';
import type { UserEntity, GetListUserEntity } from 'services/openapi';
import {
  useActiveUserMutation,
  useInactiveUserMutation,
  useRemoveUserMutation,
  useGetUsersListQuery,
  useGetUsersListExportQuery,
  useGetCompanyAllQuery,
} from 'services';
import { useGenQrBidvVAMutation } from 'services/bidvVirtualAccount/mutation/useGenQrBidvVAMutation';
import toast from 'react-hot-toast';
import 'moment/min/locales';
import { Icon } from 'components/icon';
import {
  Button,
  InputWraper,
  useForm,
  NumberUtils,
  IUseFormStructureItem,
} from 'modules';
import { InputToggleSwitch } from '../../../components/toggle-switch';
import { PopupWraper } from '../../../components/popup';
import UpdateUser from '../update';
import AddPoint from '../addPoint';
import CreateAccount from '../create';
import {
  ActiveUserSuccess,
  ActiveUserSuccessDefined,
  FilterType,
  InactiveUserSuccess,
  InactiveUserSuccessDefined,
  RemoveUserSuccess,
  RemoveUserSuccessDefined,
  convertSelectDeletedOptions,
  convertSelectRoleOptions,
  convertSelectStatusOptions,
  getInitFilterValues,
} from './configs';
import { NewRoleTypes, StatusTypes } from 'configs/constants';
import {
  checkValidActionRoleAdmin,
  checkValidRole,
  checkRolesIncludeRoles,
  getUserRoles,
} from 'utils/helpers';
import { useLocation } from 'react-router-dom';
import { Roles, RolesName, SubRolesNameOfAgency } from 'configs/roles';
import UpdatePassword from '../updatePassword';
import CreateMultiUser from '../createMultiUser';
import UpdateFinancialLimits from '../financialLimits';
import { normalizedPhoneNumber } from 'utils/helpers/helpers';
import { Actions } from 'configs/actions';

const UserList = () => {
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const initFilterValues = getInitFilterValues(urlParams);
  const [itemId, setItemId] = useState<any>();
  const [previewQrImage, setPreviewQrImage] = useState<string | null>(null);
  const [openPopUpCreateUser, setOpenPopUpCreateUser] =
    useState<boolean>(false);
  const [openPopUpCreateMultiUser, setOpenPopUpCreateMultiUser] =
    useState<boolean>(false);
  const [openPopUpUpdateUser, setOpenPopUpUpdateUser] =
    useState<boolean>(false);
  const [openPopUpRemoveUser, setOpenPopUpRemoveUser] =
    useState<boolean>(false);
  const [openPopUpAddPoint, setOpenPopUpAddPoint] = useState<boolean>(false);
  const [openPopUpUpdatePassword, setOpenPopUpUpdatePassword] =
    useState<boolean>(false);
  const [openPopUpUpdateFinancialLimit, setOpenPopUpUpdateFinancialLimit] =
    useState<boolean>(false);
  const [variables, setVariables] = useState<FilterType>(initFilterValues);
  const [refetchDataCurrentPage, setRefetchDataCurrentPage] = useState(0);
  const [dataTable, setDataTable] = useState([]);
  const userRoles = useMemo(() => getUserRoles(), []);
  const canManageUsers = useMemo(
    () =>
      checkRolesIncludeRoles(userRoles, [
        Roles.SUPER_ADMIN,
        Roles.ADMIN,
        Roles.MC,
      ]),
    [userRoles],
  );
  const canManageUserCredentials = checkRolesIncludeRoles(userRoles, [
    Roles.SUPER_ADMIN,
    Roles.ADMIN,
  ]);
  const canRemoveUser =
    canManageUserCredentials &&
    checkValidActionRoleAdmin(Actions.ManageUser_RemoveUser);

  const { data: companyInfo } = useGetCompanyAllQuery();

  // const dataCompany = companyInfo?.map((item) => ({ label: `${item.compName}- ${item.compId}`, value: item.compId })) || [];
  // const checkedDataCompany =
  //   companyInfo?.map((item) => ({
  //     label: `${item.compName}`,
  //     value: item.compId,
  //   })) || [];

  const {
    mutateAsync: mutateActiveUserAsync,
    data: dataActiveUser,
    error: errorActiveUser,
  } = useActiveUserMutation();

  const {
    mutateAsync: mutateInactiveUserAsync,
    data: dataInactiveUser,
    error: errorInactiveUser,
  } = useInactiveUserMutation();
  const {
    mutateAsync: mutateRemoveUserAsync,
    data: dataRemoveUser,
    error: errorRemoveUser,
  } = useRemoveUserMutation();

  const { mutateAsync: mutateGenQrAsync } = useGenQrBidvVAMutation();
  const [generatingQrRows, setGeneratingQrRows] = useState<Set<string>>(
    () => new Set(),
  );

  const { data: leadersList, refetch: refetchLeadersList } = {
    data: { data: [] },
    refetch: () => {},
  };

  const convertStatusValue = (status?: boolean | string) => {
    if (
      typeof status === 'undefined' ||
      status === '' ||
      status === StatusTypes['Tất cả']
    ) {
      return undefined;
    }

    if (typeof status === 'boolean') return status;

    return status === 'true';
  };

  const convertVariables = (variables: FilterType) => {
    const {
      isActive,
      isDeleted,
      role: tRole,
      searchDetailContent,
      compId,
      ...rest
    } = variables;
    let role = {};
    let objectType = {};
    if (tRole === NewRoleTypes['Merchant'] || tRole === NewRoleTypes['Admin']) {
      role = { role: tRole };
    } else if (tRole !== NewRoleTypes['Tất cả']) {
      objectType = { objectType: tRole };
      role = { role: 'Agency' };
    }
    if (compId && compId !== 'Tất cả') {
      objectType = { ...objectType, compId };
    }
    const activeStatus = convertStatusValue(isActive);
    const deletedStatus = convertStatusValue(isDeleted);

    return {
      ...rest,
      ...role,
      ...objectType,
      ...(typeof activeStatus === 'undefined'
        ? {}
        : { isActive: activeStatus }),
      ...(typeof deletedStatus === 'undefined'
        ? {}
        : { isDeleted: deletedStatus }),
      ...(searchDetailContent ? { searchDetailContent } : {}),
    };
  };

  const handleFilter = useCallback(
    (e: FilterType) => {
      setVariables({
        ...variables,
        ...e,
        // refreshData: variables.refreshData + 1,
      });
    },
    [variables],
  );

  const { isFetching: isLoadingExport, refetch } = useGetUsersListExportQuery({
    ...convertVariables(variables),
    page: DEFAULT_PAGE.toString(),
    pageSize: String((dataTable as unknown as GetListUserEntity)?.total ?? 0),
  });

  const handleChangeStatusUser = async (item: any) => {
    if (item?.isActive === false) {
      try {
        await mutateActiveUserAsync({ userId: item?.id });
        // setVariables((state) => ({ ...state, refreshData: state.refreshData + 1 }));
        // reaload page
        setRefetchDataCurrentPage((prev) => prev + 1);
      } catch (error) {
        console.log('error', error);
      }
    } else {
      try {
        await mutateInactiveUserAsync({ userId: item?.id });
        // setVariables((state) => ({ ...state, refreshData: state.refreshData + 1 }));
        // reaload page
        setRefetchDataCurrentPage((prev) => prev + 1);
      } catch (error) {
        console.log('error', error);
      }
    }
  };

  const handleOpenRemoveUser = useCallback((userId?: string) => {
    setOpenPopUpRemoveUser(true);
    setItemId(userId);
  }, []);

  const handleRemoveUser = useCallback(async () => {
    if (!canRemoveUser) {
      toast.error('Bạn không có quyền xóa người dùng');
      setOpenPopUpRemoveUser(false);
      return;
    }

    try {
      await mutateRemoveUserAsync({ userId: itemId });
      refetchLeadersList();
      setOpenPopUpRemoveUser(false);
      // setVariables((state) => ({ ...state, refreshData: state.refreshData + 1 }));
      // reaload page
      setRefetchDataCurrentPage((prev) => prev + 1);
    } catch (error) {
      console.log('error', error);
    }
  }, [canRemoveUser, itemId, mutateRemoveUserAsync, refetchLeadersList]);

  const normalizedRole = (role: string) =>
    RolesName[role as keyof typeof RolesName] ?? role;

  const normalizedSubRole = (role: string) =>
    SubRolesNameOfAgency[role as keyof typeof SubRolesNameOfAgency] ?? role;

  const formatPinDateTime = (value?: string) => {
    if (!value) return '-';

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;

    return format(date, 'HH:mm:ss dd/MM/yyyy');
  };

  const handleExportExcel = async () => {
    if (isLoadingExport) return;
    try {
      const allData = await refetch({ throwOnError: true });
      await saveListExcel(
        allData.data?.data,
        columnsDefined as ColumnDef<GetListUserEntity>[],
        'Danh_sach_nguoi_dung',
        true,
        {
          values: {
            isActive: (row: any) =>
              row.isActive ? 'Hoạt động' : 'Không hoạt động',
            vietQRImage: (row: any) => getImageSource(row.vietQRImage) || '',
          },
        },
      );
    } catch (error) {
      if (!isRequestError(error))
        toast.error(
          error instanceof Error ? error.message : 'Không thể tải danh sách',
        );
    }
  };

  useEffect(() => {
    if (dataActiveUser) {
      const { statusCode } = dataActiveUser;

      const dataDefined =
        ActiveUserSuccessDefined[statusCode as ActiveUserSuccess];

      if (statusCode === 200) {
        toast.success(dataDefined?.message, { duration: 1500 });
      }
    }
  }, [errorActiveUser, dataActiveUser]);

  useEffect(() => {
    if (dataInactiveUser) {
      const { statusCode } = dataInactiveUser;

      const dataDefined =
        InactiveUserSuccessDefined[statusCode as InactiveUserSuccess];

      if (statusCode === 200) {
        toast.success(dataDefined?.message, { duration: 1500 });
      }
    }
  }, [errorInactiveUser, dataInactiveUser]);

  useEffect(() => {
    if (dataRemoveUser) {
      const { statusCode } = dataRemoveUser;

      const dataDefined =
        RemoveUserSuccessDefined[statusCode as RemoveUserSuccess];

      if (statusCode === 200) {
        toast.success(dataDefined?.message, { duration: 1500 });
      }
    }
  }, [dataRemoveUser, errorRemoveUser]);

  const filterOptions: IUseFormStructureItem[] = [
    {
      name: 'searchString',
      label: 'Tài khoản',
      defaultValue: initFilterValues.searchString,
    },
    {
      name: 'role',
      label: 'Vai trò',
      defaultValue: initFilterValues.role,
    },
    {
      name: 'isActive',
      label: 'Trạng thái',
      defaultValue: initFilterValues.isActive,
    },
    {
      name: 'isDeleted',
      label: 'Hoạt động',
      defaultValue: initFilterValues.isDeleted,
    },
    {
      name: 'compId',
      label: 'Doanh nghiệp/HTX',
      defaultValue: initFilterValues.compId,
    },
  ];

  const columnsDefined: ColumnDef<UserEntity>[] = [
    {
      accessorKey: 'phoneNumber',
      header: 'Tài khoản',
      cell: (info) => normalizedPhoneNumber(String(info.getValue())),
    },
    {
      accessorKey: 'fullName',
      header: 'Tên tài khoản',
    },
    {
      accessorKey: 'citizenIdentityCardNo',
      header: 'CCCD/MST',
    },
    {
      accessorKey: 'bidvVaPrefix',
      header: 'Mã đầu Tiểu khoản BIDV',
    },
    {
      accessorKey: 'bidvVaPayerId',
      header: 'Tiểu khoản BIDV',
    },
    {
      accessorKey: 'role',
      header: 'Vai trò',
      cell: (info) => {
        if (info.getValue() !== Roles.AGENCY) {
          return (
            <div style={{ width: 150 }}>
              {normalizedRole(String(info.getValue()))}
            </div>
          );
        } else {
          return (
            <div style={{ width: 150 }}>
              {normalizedSubRole(String(info.row.original.objectType))}
            </div>
          );
        }
      },
    },
    {
      accessorKey: 'compName',
      header: 'Doanh nghiệp/HTX',
      cell: (info) => {
        return <div style={{ width: 150 }}>{String(info.getValue())}</div>;
      },
    },
    {
      accessorKey: 'balance',
      header: 'Số dư (đ)',
      cell: (info) => NumberUtils.toFormatNumber(Number(info.getValue())),
    },
    {
      accessorKey: 'email',
      header: 'Email',
    },
    {
      accessorKey: 'myReferralId',
      header: 'Mã giới thiệu',
    },
    {
      accessorKey: 'isActive',
      header: 'Trạng thái',
      cell: (info) => {
        return (
          <InputToggleSwitch
            onChange={() =>
              canManageUsers &&
              checkValidActionRoleAdmin(Actions.ManageUser_ActiveUser) &&
              checkValidActionRoleAdmin(Actions.ManageUser_DeactiveUser) &&
              handleChangeStatusUser(info.row.original)
            }
            value={info.getValue()}
            onTouched={() => false}
            label=''
            name=''
          />
        );
      },
    },
    {
      accessorKey: 'pinLastFailedAt',
      header: 'PIN sai gần nhất',
      cell: (info) => (
        <div style={{ width: 150 }}>
          {formatPinDateTime(info.getValue() as string | undefined)}
        </div>
      ),
    },
    {
      accessorKey: 'pinLastLockedAt',
      header: 'PIN bị khóa gần nhất',
      cell: (info) => (
        <div style={{ width: 150 }}>
          {formatPinDateTime(info.getValue() as string | undefined)}
        </div>
      ),
    },
    {
      accessorKey: 'vietQRImage',
      header: 'Mã QR BIDV',
      cell: (info) => {
        const vietQRImage = getImageSource(info.getValue() as string);
        const rowData = info.row.original;
        const qrRowKey =
          rowData.id ??
          JSON.stringify([
            rowData.compId,
            rowData.bidvVaPrefix,
            rowData.bidvVaPayerId,
          ]);

        if (vietQRImage) {
          // Nếu có QR image thì hiển thị image
          return (
            <div style={{ textAlign: 'center' }}>
              <button
                type='button'
                className='ImageModalTrigger'
                aria-label='Xem mã QR BIDV'
                onClick={(event) => {
                  event.stopPropagation();
                  setPreviewQrImage(vietQRImage);
                }}
              >
                <img
                  src={vietQRImage}
                  alt='Mã QR BIDV'
                  style={{
                    width: '60px',
                    height: '80px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                  }}
                />
              </button>
            </div>
          );
        } else {
          // Nếu chưa có QR image thì hiển thị button Gen QR
          return (
            <div style={{ textAlign: 'center' }}>
              <Button
                className='whitespace-nowrap'
                label='Gen QR'
                style={{
                  whiteSpace: 'nowrap',
                }}
                buttonType='info'
                isLoading={generatingQrRows.has(qrRowKey)}
                disabled={generatingQrRows.has(qrRowKey)}
                onClick={async () => {
                  try {
                    if (!rowData.bidvVaPrefix || !rowData.bidvVaPayerId) {
                      toast.error(
                        'Thiếu thông tin Mã đầu Tiểu khoản hoặc Tiểu khoản BIDV',
                      );
                      return;
                    }
                    setGeneratingQrRows((prev) => new Set(prev).add(qrRowKey));
                    await mutateGenQrAsync({
                      vaPrefix: rowData.bidvVaPrefix || '',
                      endCode: rowData.bidvVaPayerId || '',
                    });
                    toast.success('Tạo mã QR thành công!');
                    setRefetchDataCurrentPage((prev) => prev + 1);
                  } catch (error) {
                    if (!isRequestError(error)) {
                      toast.error('Có lỗi xảy ra khi tạo mã QR');
                    }
                  } finally {
                    setGeneratingQrRows((prev) => {
                      const next = new Set(prev);
                      next.delete(qrRowKey);
                      return next;
                    });
                  }
                }}
              />
            </div>
          );
        }
      },
    },
    {
      id: 'action',
      header: 'Thao tác',
      cell: (info) =>
        canManageUsers && (
          <div className='action'>
            {checkValidActionRoleAdmin(Actions.ManageUser_UpdateUser) && (
              <div
                className='action__update'
                onClick={() => {
                  setOpenPopUpUpdateUser(true);
                  setItemId(info.row.original.id);
                }}
              >
                <Icon.ActionEditIcon />
              </div>
            )}

            {canRemoveUser && (
              <div
                className='action__remove'
                onClick={() => handleOpenRemoveUser(info.row.original.id)}
              >
                <Icon.Trash />
              </div>
            )}

            {/* {checkValidActionRoleAdmin(Actions.ManageUser_AddPointToUser) && (
              <div
                className='action__plusminus'
                onClick={() => {
                  setOpenPopUpAddPoint(true);
                  setItemId(info.row.original.id);
                }}
              >
                <Icon.Plus />
              </div>
            )} */}

            {canManageUserCredentials &&
              checkValidActionRoleAdmin(
                Actions.ManageUser_UpdateUserPassword,
              ) && (
                <div
                  className='action__updatepassword'
                  onClick={() => {
                    setOpenPopUpUpdatePassword(true);
                    setItemId(info.row.original.id);
                  }}
                >
                  <Icon.Setting />
                </div>
              )}

            {checkValidActionRoleAdmin(
              Actions.ManageUser_UpdateFinancialLimit,
            ) && (
              <div
                className='action__financial-limit'
                title='Quản lý hạn mức'
                onClick={() => {
                  setOpenPopUpUpdateFinancialLimit(true);
                  setItemId(info.row.original.id);
                }}
              >
                <Icon.Wallet />
              </div>
            )}
          </div>
        ),
    },
  ];

  const { getInputProps, handleSubmit } = useForm({
    structure: filterOptions,
    onSubmit: handleFilter,
  });

  return (
    <>
      <ImageModal
        isOpen={!!previewQrImage}
        src={previewQrImage}
        title='Mã QR BIDV'
        alt='Mã QR BIDV'
        onClose={() => setPreviewQrImage(null)}
      />
      <Card className='container-fluid mb20'>
        <form onSubmit={handleSubmit}>
          <div className='row'>
            <div className='col-lg-3 col-12'>
              <InputWraper
                inputProps={getInputProps('searchString')}
                renderInput={InputText}
              />
            </div>

            <div className='col-lg-3 col-12'>
              <InputWraper
                inputProps={getInputProps('role')}
                renderInput={(props) => (
                  <InputSelect
                    {...props}
                    options={convertSelectRoleOptions(userRoles)}
                  />
                )}
              />
            </div>
            <div className='col-lg-3 col-12'>
              <InputWraper
                inputProps={getInputProps('isActive')}
                renderInput={(props) => (
                  <InputSelect
                    {...props}
                    options={convertSelectStatusOptions()}
                  />
                )}
              />
            </div>
            <div className='col-lg-3 col-12'>
              <InputWraper
                inputProps={getInputProps('compId')}
                renderInput={(props) => (
                  <InputSelect
                    {...props}
                    options={[
                      { label: 'Tất cả', value: 'Tất cả' },
                      ...(companyInfo?.map((item) => ({
                        label: `${item.compName}`,
                        value: item.compId,
                      })) || []),
                    ]}
                  />
                )}
              />
            </div>
            <div className='col-lg-3 col-12'>
              <InputWraper
                inputProps={getInputProps('isDeleted')}
                renderInput={(props) => (
                  <InputSelect
                    {...props}
                    options={convertSelectDeletedOptions()}
                  />
                )}
              />
            </div>
          </div>
          {checkValidActionRoleAdmin(Actions.ManageUser_ListUser) && (
            <div className='textCenter mt16 wrapButtonFilter'>
              <Button label={'Tìm kiếm'} type='submit' buttonType='success' />
              <Button
                className='ml15'
                onClick={handleExportExcel}
                label={'Tải về DS'}
                type='submit'
                buttonType='info'
                isLoading={isLoadingExport}
              />
            </div>
          )}
        </form>
      </Card>

      {/* {getUserRoles().includes("Admin") && (
        <div className="textCenter">
          <Button className="mb20 " label={"Tạo mới tài khoản"} onClick={() => setOpenPopUpCreateUser(true)} buttonType="success" />
        </div>
      )} */}

      <TableListData<GetListUserEntity>
        columns={columnsDefined as ColumnDef<GetListUserEntity>[]}
        useQuery={useGetUsersListQuery}
        variables={variables}
        refetchDataCurrentPage={refetchDataCurrentPage}
        setVariables={setVariables}
        convertVariables={convertVariables}
        filterKey='searchDetailContent'
        orderColumn
        callBackData={setDataTable}
        customHeaderBtnGroup={() => (
          <>
            {checkValidRole([Roles.SUPER_ADMIN, Roles.ADMIN, Roles.MC]) && (
              <div
                className='textCenter ml5 mt20'
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 5,
                }}
              >
                {(checkValidActionRoleAdmin(Actions.ManageUser_CreateUser) ||
                  checkValidRole([Roles.MC])) && (
                  <Button
                    className='mb20'
                    label={'Tạo mới tài khoản'}
                    onClick={() => setOpenPopUpCreateUser(true)}
                    buttonType='success'
                  />
                )}
                {(checkValidActionRoleAdmin(
                  Actions.ManageUser_CreateMultipleUser,
                ) ||
                  checkValidRole([Roles.MC])) && (
                  <Button
                    className='mb20'
                    label={'Tạo nhiều tài khoản'}
                    onClick={() => setOpenPopUpCreateMultiUser(true)}
                    buttonType='success'
                  />
                )}
              </div>
            )}
          </>
        )}
        customTopRow={(data: any) => {
          if (data) {
            return (
              <>
                <tr>
                  <td colSpan={6}></td>
                  <td>
                    <strong>TỔNG</strong>
                  </td>
                  <td>
                    <strong>
                      {NumberUtils.toFormatNumber(
                        Number(data?.totalBalance ?? 0),
                      )}
                    </strong>
                  </td>
                  <td></td>
                  <td></td>
                  <td></td>
                </tr>
              </>
            );
          }
        }}
      />

      {openPopUpCreateUser && (
        <CreateAccount
          onClose={() => setOpenPopUpCreateUser(false)}
          refetchLeadersList={() => refetchLeadersList()}
          forceUpdateTable={() =>
            setVariables((state) => ({
              ...state,
              refreshData: state.refreshData + 1,
            }))
          }
          companyInfo={companyInfo}
        />
      )}
      {openPopUpCreateMultiUser && (
        <CreateMultiUser
          onClose={() => setOpenPopUpCreateMultiUser(false)}
          forceUpdateTable={() =>
            setVariables((state) => ({
              ...state,
              refreshData: state.refreshData + 1,
            }))
          }
          companyInfo={companyInfo}
        />
      )}
      {openPopUpUpdateUser && (
        <UpdateUser
          userId={itemId}
          onClose={() => setOpenPopUpUpdateUser(false)}
          refetchLeadersList={() => refetchLeadersList()}
          forceUpdateTable={() => setRefetchDataCurrentPage((prev) => prev + 1)}
          companyInfo={companyInfo}
        />
      )}
      {openPopUpRemoveUser && (
        <PopupWraper
          center
          title='Xóa tài khoản'
          onClose={() => setOpenPopUpRemoveUser(false)}
        >
          <div className='container'>
            <div className='row'>
              <div className='col-12'>
                <div className='label'>
                  Bạn có chắc chắn muốn xóa tài khoản này không?
                </div>
              </div>
            </div>
          </div>

          <div className='wrap-button-remove-user'>
            <Button
              label='Xóa'
              buttonType='success'
              onClick={handleRemoveUser}
            />
            <Button
              label='Hủy'
              buttonType='grey'
              onClick={() => setOpenPopUpRemoveUser(false)}
            />
          </div>
        </PopupWraper>
      )}
      {openPopUpAddPoint && (
        <AddPoint
          onClose={() => setOpenPopUpAddPoint(false)}
          userId={itemId}
          forceUpdateTable={() => setRefetchDataCurrentPage((prev) => prev + 1)}
        />
      )}
      {openPopUpUpdatePassword && (
        <UpdatePassword
          onClose={() => setOpenPopUpUpdatePassword(false)}
          userId={itemId}
          forceUpdateTable={() => setRefetchDataCurrentPage((prev) => prev + 1)}
        />
      )}
      {openPopUpUpdateFinancialLimit && (
        <UpdateFinancialLimits
          onClose={() => setOpenPopUpUpdateFinancialLimit(false)}
          userId={itemId}
          forceUpdateTable={() => setRefetchDataCurrentPage((prev) => prev + 1)}
        />
      )}
    </>
  );
};

export default UserList;
